import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAutocomplete } from '@/hooks/use-autocomplete';
import { formatMoney } from '@/lib/utils';
import { search as searchRoute } from '@/routes';
import { show as showClassified } from '@/routes/classifieds';
import { show as showListing } from '@/routes/listings';
import type { AutocompleteResult } from '@/types';
import { router } from '@inertiajs/react';
import { Loader2, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface SearchAutocompleteProps {
    onSearch?: (query: string) => void;
    onChange?: (query: string) => void;
    placeholder?: string;
    className?: string;
    showButton?: boolean;
    buttonText?: string;
    initialValue?: string;
    searchType?: 'embarcacoes' | 'empresas';
}

export function SearchAutocomplete({
    onSearch,
    onChange,
    placeholder = 'Busque por marca, modelo ou local',
    className = '',
    showButton = true,
    buttonText = 'Pesquisar',
    initialValue = '',
    searchType,
}: SearchAutocompleteProps) {
    const [query, setQuery] = useState(initialValue);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listboxId = useRef(`autocomplete-list-${Math.random().toString(36).substr(2, 9)}`).current;

    const { results, categories, total, loading, search, clearResults } = useAutocomplete({
        minChars: 2,
        debounceMs: 300,
        scope: searchType === 'empresas' ? 'listing' : searchType === 'embarcacoes' ? 'classified' : 'all',
    });

    const handleInputChange = (value: string) => {
        setQuery(value);
        setSelectedIndex(-1);
        search(value);
        if (value.length >= 2) {
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }

        // Notify parent component of value change
        if (onChange) {
            onChange(value);
        }
    };

    const handleSelectResult = (result: AutocompleteResult) => {
        if (result.type === 'listing') {
            router.visit(showListing(result.slug).url);
        } else {
            router.visit(showClassified(result.slug).url);
        }
        setQuery('');
        setIsOpen(false);
        clearResults();
    };

    const handleSearch = () => {
        if (query.trim()) {
            if (onSearch) {
                onSearch(query);
            } else {
                const params: Record<string, any> = { search: query };
                if (searchType) {
                    params.type = searchType;
                }
                router.get(searchRoute().url, params);
            }
            setIsOpen(false);
        }
    };

    const handleClear = () => {
        setQuery('');
        setIsOpen(false);
        clearResults();
        inputRef.current?.focus();

        // Notify parent component of value change
        if (onChange) {
            onChange('');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen || results.length === 0) {
            if (e.key === 'Enter') {
                handleSearch();
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && results[selectedIndex]) {
                    handleSelectResult(results[selectedIndex]);
                } else {
                    handleSearch();
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSelectedIndex(-1);
                break;
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            <div className="flex flex-col gap-3 lg:flex-row">
                <div className="relative flex-1 bg-white">
                    <Input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className="h-12 flex-1 rounded-lg border border-gray-300 px-4 pr-10"
                        role="combobox"
                        aria-expanded={isOpen}
                        aria-controls={listboxId}
                        aria-autocomplete="list"
                        aria-activedescendant={selectedIndex >= 0 ? `${listboxId}-item-${selectedIndex}` : undefined}
                    />
                    {query && (
                        <button
                            onClick={handleClear}
                            className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            aria-label="Limpar pesquisa"
                            type="button"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>

                {showButton && (
                    <Button
                        onClick={handleSearch}
                        className="h-12 cursor-pointer rounded-lg bg-blue-800 px-8 text-lg font-medium text-white hover:bg-blue-900"
                        type="button"
                    >
                        {buttonText}
                        <Search className="ml-2 h-4 w-4" />
                    </Button>
                )}
            </div>

            {/* Autocomplete Dropdown */}
            {isOpen && (
                <div
                    id={listboxId}
                    role="listbox"
                    className="absolute z-50 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                    aria-label="Resultados da busca"
                >
                    {loading && (
                        <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
                            <Loader2 className="h-6 w-6 animate-spin text-blue-800" />
                            <span className="sr-only">Carregando resultados...</span>
                        </div>
                    )}

                    {!loading && results.length === 0 && categories.length === 0 && query.length >= 2 && (
                        <div className="px-4 py-8 text-center text-gray-500" role="status" aria-live="polite">
                            Nenhum resultado encontrado para "{query}"
                        </div>
                    )}

                    {!loading && (categories.length > 0 || results.length > 0) && (
                        <>
                            <div className="max-h-96 overflow-y-auto">
                                {/* Categories */}
                                {categories.length > 0 && (
                                    <>
                                        <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Categorias</div>
                                        {categories.map((category, index) => (
                                            <button
                                                key={`category-${category.id}`}
                                                id={`${listboxId}-category-${index}`}
                                                role="option"
                                                aria-selected={false}
                                                onClick={() => {
                                                    // Use friendly URL if available, otherwise fallback to query parameter
                                                    if (category.friendly_url_path && category.module === 'classified') {
                                                        router.get(category.friendly_url_path);
                                                    } else if (category.module === 'listing') {
                                                        // For listing categories, navigate to a listing search or category page
                                                        router.get('/empresas', { categoria: category.slug });
                                                    } else {
                                                        // For classified categories, use the existing search
                                                        router.get(searchRoute().url, { categoria: category.slug });
                                                    }
                                                    setQuery('');
                                                    setIsOpen(false);
                                                    clearResults();
                                                }}
                                                className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 transition-colors hover:bg-blue-50"
                                                type="button"
                                            >
                                                <div
                                                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full ${
                                                        category.module === 'listing' ? 'bg-green-100' : 'bg-blue-100'
                                                    }`}
                                                >
                                                    {category.logo ? (
                                                        <img src={category.logo} alt={category.name} className="h-full w-full object-cover" />
                                                    ) : (
                                                        <Search
                                                            className={`h-5 w-5 ${
                                                                category.module === 'listing' ? 'text-green-600' : 'text-blue-600'
                                                            }`}
                                                        />
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1 text-left">
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className="font-medium text-gray-900"
                                                            dangerouslySetInnerHTML={{ __html: category.highlighted_name }}
                                                        />
                                                        <span
                                                            className={`rounded-full px-2 py-1 text-xs font-medium ${
                                                                category.module === 'listing'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-blue-100 text-blue-800'
                                                            }`}
                                                        >
                                                            {category.module === 'listing' ? 'Empresas' : 'Anúncios'}
                                                        </span>
                                                    </div>
                                                    <div className="truncate text-xs text-gray-500">{category.path}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </>
                                )}

                                {/* Results */}
                                {results.length > 0 && (
                                    <>
                                        {categories.length > 0 && (
                                            <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Resultados</div>
                                        )}
                                        {results.map((result, index) => (
                                            <button
                                                key={result.id}
                                                id={`${listboxId}-item-${index}`}
                                                role="option"
                                                aria-selected={selectedIndex === index}
                                                onClick={() => handleSelectResult(result)}
                                                className={`flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-gray-50 ${
                                                    selectedIndex === index ? 'bg-gray-50' : ''
                                                }`}
                                                type="button"
                                            >
                                                {/* Thumbnail */}
                                                <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded bg-gray-200" aria-hidden="true">
                                                    {result.thumbnail ? (
                                                        <img src={result.thumbnail} alt="" className="h-full w-full object-cover" loading="lazy" />
                                                    ) : (
                                                        <div className="flex h-full w-full items-center justify-center text-gray-400">
                                                            <Search className="h-6 w-6" />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Content */}
                                                <div className="min-w-0 flex-1 text-left">
                                                    {/* Meilisearch highlighting is safe - only uses <em> tags */}
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className="truncate font-medium text-gray-900"
                                                            dangerouslySetInnerHTML={{ __html: result.highlighted_title }}
                                                        />
                                                        <span
                                                            className={`rounded-full px-2 py-1 text-xs font-medium ${
                                                                result.type === 'listing'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-blue-100 text-blue-800'
                                                            }`}
                                                        >
                                                            {result.type === 'listing' ? 'Empresa' : 'Embarcação'}
                                                        </span>
                                                    </div>
                                                    <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                                                        {result.price && (
                                                            <span className="font-semibold text-blue-800">{formatMoney(result.price)}</span>
                                                        )}
                                                        {result.location && (
                                                            <>
                                                                {result.price && <span aria-hidden="true">•</span>}
                                                                <span>{result.location}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </>
                                )}
                            </div>

                            {/* Footer */}
                            {total > results.length + categories.length && (
                                <button
                                    onClick={handleSearch}
                                    className="w-full border-t border-gray-200 px-4 py-3 text-center text-sm font-medium text-blue-800 hover:bg-blue-50"
                                    type="button"
                                >
                                    Ver todos os {total} resultados
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
